import React from 'react';
import Loading from '../../components/loading';
import PropTypes from 'prop-types';

import { WeezHeader, Button, FrameCard, ToastContext } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import EventModel from '../../models/eventModel';
import { ReactComponent as ReelaxLogo } from '../../nacre/images/reelax-ticket.svg';
import { ReactComponent as TicketswapLogo } from '../../nacre/images/ticketswap.svg';
import { ReactComponent as PasseTonBilletLogo } from '../../nacre/images/passetonbillet.svg';
import ResalerSegment from './resalerSegment';
import ResaleConfigBlock from './resaleConfigBlock';
import ResaleConfigModel from '../../models/resaleConfigModel';
import UserRoleOrganizationModel from '../../models/userRoleOrganizationModel';

export function Home({ title, onSave, onCancel }) {
    const { t } = useTranslation();

    return (
        <WeezHeader
            backLink={{ onClick: onCancel }}
            title={title}
            rightComponent={[
                <Button primary key="primary" label={t('ticket.common.action.save')} onClick={onSave} />,
                <Button key="secondary" label={t('ticket.common.action.cancel')} onClick={onCancel} />
            ]}
        />
    );
}

Home.propTypes = {
    title: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default function Resales({ organizationId, history }) {
    const { t } = useTranslation();
    let [resellerActivated, setReseller] = React.useState({ TicketSwap: false, Reelax: false, PasseTonBillet: false });
    let [loading, setLoading] = React.useState(true);
    const toast = React.useContext(ToastContext);
    let [mainConfig, setMainConfig] = React.useState([]);
    let [configExceptions, setConfigExceptions] = React.useState([]);
    let [configExceptionsToDelete, setConfigExceptionsToDelete] = React.useState([]);

    const RESELLER_HARDCODED = {
        TicketSwap: {
            logo: <TicketswapLogo className={'resaler_logo'} />,
            title: 'ticket.resales.resalers.ticketSwap.title',
            information: 'ticket.resales.resalers.ticketSwap.information',
            link: 'ticket.resales.resalers.ticketSwap.link',
            warning: 'ticket.resales.resalers.ticketSwap.warning'
        },
        Reelax: {
            logo: <ReelaxLogo className={'resaler_logo'} />,
            title: 'ticket.resales.resalers.reelax.title',
            information: 'ticket.resales.resalers.reelax.information',
            link: 'ticket.resales.resalers.reelax.link',
            warning: null
        },
        PasseTonBillet: {
            logo: <PasseTonBilletLogo className={'resaler_logo'} />,
            title: 'ticket.resales.resalers.passetonbillet.title',
            information: 'ticket.resales.resalers.passetonbillet.information',
            link: 'ticket.resales.resalers.passetonbillet.link',
            warning: null
        }
    };

    /**
     * Action on cancel button header
     */
    const onCancel = React.useCallback(() => {
        return history.push(`/ticket/O${organizationId}/events`);
    }, [history, organizationId]);

    const loadResales = React.useCallback(() => {
        return ResaleConfigModel.getAll({ organizationId })
            .then(response => {
                // Make the mapping to have exceptions well formated
                let [main_config_filter] = response.filter(config => !config.event && !config.group && !config.rate).map(config => config.object);
                let exceptions_config_filter = response.filter(config => config.event || config.group || config.rate).map(config => config.object);
                if (!main_config_filter) {
                    main_config_filter = { rate: null, event: null, group: null, organization: organizationId, enabled: true };
                }
                setMainConfig(main_config_filter);
                setConfigExceptions(exceptions_config_filter);
                return response;
            })
            .catch(e => {
                toast.error('ticket.resales.config.retrieve_error');
            });
    }, [organizationId]);

    const [events, loadingEvents, syncEvents] = EventModel.useApiModel(
        {
            organizationId,
            query: {
                time_status: ['upcoming', 'ongoing'],
                publication_status: ['draft', 'published']
            },
            onError: () => toast.error(t('ticket.event.get_error'))
        },
        [organizationId]
    );

    const sync = React.useCallback(() => {
        let rightsTmp = { TicketSwap: false, Reelax: false };

        // Get role of ticketswap and reelax
        // /users/app_UUID_APP
        let userroles = UserRoleOrganizationModel.getAll({ role: 'ticketswap', organizationId: organizationId })
            .then(response => {
                response.forEach(userRole => {
                    if (userRole.context.length === 3 && userRole.context[2] === String(organizationId)) {
                        let resellerTmp = window.weezConfig.resellers.find(obj => obj.id === userRole.user.username);
                        if (resellerTmp) {
                            rightsTmp[resellerTmp.name] = true;
                        }
                    }
                });
                setReseller(rightsTmp);
            })
            .catch(e => {
                toast.error('ticket.resales.resalers.retrieve_error');
            });

        return Promise.all([userroles]);
    }, [organizationId]);

    /**
     * On changing one reseller
     */
    let onChangeReseller = React.useCallback(
        (name, value) => {
            let rightsTmp = { ...resellerActivated };
            rightsTmp[name] = value;
            setReseller(rightsTmp);
            let any_active = Object.values(resellerActivated).some(v => v);
            if (value && !any_active) {
                setMainConfig({ rate: null, event: null, group: null, organization: organizationId, enabled: true });
            }
        },
        [resellerActivated, setReseller]
    );

    const reload = React.useCallback(() => {
        setLoading(true);
        syncEvents();
        sync()
            .then(([user_roles]) => {
                return loadResales();
            })
            .finally(() => {
                setLoading(false);
                setConfigExceptionsToDelete(configExceptionsToDelete || []);
            });
    }, [sync, loadResales, syncEvents]);

    /**
     * Action on save button header
     */
    const onSave = React.useCallback(() => {
        // Create
        let dataToCreate = [];
        let promises = [];

        if (mainConfig.enabled === null) {
            return toast.error(t('ticket.resales.config.save_action_no_orga_config'));
        }

        configExceptions
            .filter(config => !config.id)
            .forEach(config => {
                dataToCreate.push(new ResaleConfigModel(config));
            });

        if (!mainConfig.id) {
            dataToCreate.push(new ResaleConfigModel(mainConfig));
        }
        if (dataToCreate.length) {
            promises.push(ResaleConfigModel.save(dataToCreate, { organizationId }));
        }

        // Update
        let dataToUpdate = [];
        configExceptions
            .filter(config => config.id)
            .forEach(config => {
                dataToUpdate.push(new ResaleConfigModel(config));
            });
        if (mainConfig.id) {
            dataToUpdate.push(new ResaleConfigModel(mainConfig));
        }
        if (dataToUpdate.length) {
            promises.push(ResaleConfigModel.save(dataToUpdate, { organizationId }));
        }

        // Delete
        promises.push(...configExceptionsToDelete.map(obj => new ResaleConfigModel(obj).delete({ organizationId })));

        Promise.all(promises)
            .then(() => toast.success(t('ticket.resales.config.save_action_success')))
            .catch(e => toast.error(t('ticket.resales.config.save_action_error')))
            .finally(() => reload());
    }, [configExceptions, configExceptionsToDelete, organizationId, mainConfig, reload]);

    const onDeleteException = React.useCallback(configToDelete => {
        let toDeleteArray = configExceptionsToDelete.concat(configToDelete);
        setConfigExceptionsToDelete(toDeleteArray);
    });

    React.useEffect(() => {
        reload();
    }, []);

    if (loading || loadingEvents) {
        return <Loading />;
    }

    let hasActiveReseller = Object.values(resellerActivated).some(v => v);

    return (
        <div>
            <Home title={t('ticket.resales.title')} onSave={onSave} onCancel={onCancel} />
            <FrameCard title={t('ticket.resales.resalers.title')} subtitle={t('ticket.resales.resalers.subtitle')}>
                {window.weezConfig.resellers.map(reseller => {
                    let info = RESELLER_HARDCODED[reseller.name];
                    if (!info) {
                        return null;
                    }
                    let { title, logo, information, link, warning } = info;
                    return (
                        <ResalerSegment
                            key={reseller.id}
                            id={reseller.id}
                            name={reseller.name}
                            title={title}
                            icon={logo}
                            isChecked={resellerActivated[reseller.name]}
                            information={information}
                            link={t([link, '#'])}
                            onChange={onChangeReseller}
                            onError={sync}
                            warning={warning}
                            organizationId={organizationId}
                        />
                    );
                })}
            </FrameCard>
            <ResaleConfigBlock
                eventList={events}
                organizationId={organizationId}
                hasActiveReseller={hasActiveReseller}
                mainConfig={mainConfig}
                onResaleModeChange={setMainConfig}
                configExceptions={configExceptions}
                setConfigExceptions={setConfigExceptions}
                setConfigExceptionsToDelete={onDeleteException}
            />
        </div>
    );
}

Resales.propTypes = {
    history: PropTypes.object.isRequired,
    organizationId: PropTypes.string.isRequired
};
